import { LocalStorageKey } from "@/constants/constants";
import {
  setDeviceInfo,
  setServerHash,
  setTwaReady,
  updateTwaAdjustData,
  updateTwaPlayinstallData,
} from "@/stores/sysConfigStore";

import { getLocalStorageItem, setLocalStorageItem } from "./localStorage";

export interface TwaMessage {
  type: string;
  data?: Record<string, unknown>;
}

class TwaBridge {
  private static instance: TwaBridge;
  private twaPort: MessagePort | null = null;
  private isConnected = false;
  private messageHandler: ((data: Record<string, unknown>) => void) | null =
    null;
  private _isInitialized = false;

  private constructor() {
    // 不在构造函数中初始化，改为主动调用
  }

  public static getInstance(): TwaBridge {
    if (!TwaBridge.instance) {
      TwaBridge.instance = new TwaBridge();
    }
    return TwaBridge.instance;
  }

  // 建立 TWA 通信
  public setupCommunication(): void {
    const twaInit: Record<string, string> = getLocalStorageItem(
      LocalStorageKey.TwaInit,
      {},
    );
    if (this._isInitialized) {
      console.log("[TWA] 通信已初始化，跳过重复初始化");
      return;
    }

    if (typeof window === "undefined") {
      twaInit.ssrBlock = "1";
      console.warn("[TWA] SSR 环境，跳过 TWA 通信初始化");
      return;
    }
    twaInit.twa_is_init = "1";

    this._isInitialized = true;

    window.addEventListener("message", (event) => {
      const port = event.ports[0];
      if (!port) return;

      this.twaPort = port;
      console.log("[TWA] 获取到消息端口");

      // 发送连接确认
      port.postMessage("Connected");

      // 设置消息处理器
      port.onmessage = (event) => {
        try {
          console.log("[TWA] 收到原始消息:", event.data, typeof event.data);

          let data;
          if (typeof event.data === "string") {
            if (event.data === "") {
              console.log("[TWA] 收到空字符串消息，跳过处理");
              return;
            }
            try {
              data = JSON.parse(event.data);
            } catch (parseError) {
              console.log("[TWA] JSON 解析失败，使用原始字符串:", event.data);
              data = { type: "raw_message", content: event.data };
            }
          } else {
            data = event.data;
          }

          this.handleMessage(data);
        } catch (error) {
          console.error("[TWA] 消息处理错误:", error);
        }
      };

      console.log("[TWA] 通信通道建立成功");
    });
    setLocalStorageItem(LocalStorageKey.TwaInit, twaInit);
  }

  // 处理 TWA 消息
  private handleMessage(data: Record<string, unknown>): void {
    console.log("[TWA] 收到消息:", data);
    const twaInit: Record<string, unknown> = getLocalStorageItem(
      LocalStorageKey.TwaInit,
      {},
    );

    switch (data.type) {
      case "adjust_attribution":
        this.isConnected = true;
        console.log("[TWA] 归因数据:", data);
        if (
          data.param &&
          typeof data.param === "object" &&
          !("error" in data.param)
        ) {
          updateTwaAdjustData(data.param as Record<string, unknown>);
        } else {
          console.error("[TWA] adjust_attribution 数据格式错误:", data);
        }
        twaInit.adjust_attribution = JSON.stringify(data);
        break;

      case "native_ready":
        console.log("[TWA] 连接确认 native_ready");
        twaInit.native_ready = JSON.stringify(data);
        this.isConnected = true;
        break;

      case "playinstall_referrer":
        this.isConnected = true;
        console.log("[TWA] Play Install Referrer:", data);
        if (
          data.param &&
          typeof data.param === "object" &&
          !("error" in data.param)
        ) {
          updateTwaPlayinstallData(data.param as Record<string, unknown>);
        } else {
          console.error("[TWA] playinstall_referrer 数据格式错误:", data);
        }
        twaInit.playinstall_referrer = JSON.stringify(data);
        break;

      case "connected":
        console.log("[TWA] 连接确认");
        this.isConnected = true;
        twaInit.connected = JSON.stringify(data);
        break;

      case "playintegrity_token":
        this.isConnected = true;
        console.log("[TWA] Play Integrity Token:", data);
        if (
          data.param &&
          typeof data.param === "object" &&
          !("error" in data.param) &&
          "token" in data.param
        ) {
          setServerHash(data.param.token as string);
        } else {
          console.error("[TWA] Play Integrity Token 数据格式错误:", data);
        }
        twaInit.playintegrity_token = JSON.stringify(data);
        break;
      case "device_info":
        this.isConnected = true;
        if (
          data.param &&
          typeof data.param === "object" &&
          !("error" in data.param)
        ) {
          setDeviceInfo(data.param as Record<string, unknown>);
        } else {
          console.error("[TWA] device_info 数据格式错误:", data);
        }
        twaInit.device_info = JSON.stringify(data);
        break;

      default:
        console.log("[TWA] 未知消息类型:", data.type);
        break;
    }
    setLocalStorageItem(LocalStorageKey.TwaInit, twaInit);

    if (this.isConnected) {
      setTwaReady(true);
      const twaInit: Record<string, unknown> = getLocalStorageItem(
        LocalStorageKey.TwaInit,
        {},
      );
      twaInit.twa_connected = "1";
      setLocalStorageItem(LocalStorageKey.TwaInit, twaInit);
    }
    // 发送确认消息
    if (this.twaPort) {
      this.twaPort.postMessage(`ACK ${data.type}`);
    }

    // 调用自定义消息处理器
    if (this.messageHandler) {
      this.messageHandler(data);
    }
  }

  // 请求 TWA 数据
  public requestData(type: string, data?: Record<string, unknown>): void {
    console.log("[TWA] 请求数据:", type, this.twaPort, this.isConnected);
    const twaInit: Record<string, string> = getLocalStorageItem(
      LocalStorageKey.TwaInit,
      {},
    );
    if (!this.twaPort || !this.isConnected) {
      twaInit.request_data_connected = "false";
      twaInit.request_data_port = "false";
      twaInit.request_data_type = type;
      setLocalStorageItem(LocalStorageKey.TwaInit, twaInit);
      console.warn("[TWA] 通信未建立，无法请求数据");
      return;
    }

    // 尝试不同的消息格式
    const messageObj = { type: type, param: data };
    const messageStr = JSON.stringify(messageObj);

    // console.log("[TWA] 发送对象消息:", messageObj);

    // // 先尝试对象格式
    // this.twaPort.postMessage(messageObj);
    console.log("[TWA] 发送字符串消息:", messageStr);

    //如果对象格式不行，可以尝试字符串格式
    this.twaPort.postMessage(messageStr);
  }

  // 发送消息给 TWA
  public sendMessage(message: TwaMessage): void {
    if (!this.twaPort || !this.isConnected) {
      console.warn("[TWA] 通信未建立，无法发送消息");
      return;
    }

    this.twaPort.postMessage(message);
  }

  // 检查连接状态
  public isTwaConnected(): boolean {
    return this.isConnected;
  }

  // 检查是否已初始化
  public isInitialized(): boolean {
    return this._isInitialized;
  }

  // 获取端口引用（用于高级操作）
  public getPort(): MessagePort | null {
    return this.twaPort;
  }

  // 设置自定义消息处理器
  public setMessageHandler(
    handler: (data: Record<string, unknown>) => void,
  ): void {
    this.messageHandler = handler;
  }

  // 清理连接
  public destroy(): void {
    if (this.twaPort) {
      this.twaPort.close();
      this.twaPort = null;
    }
    this.isConnected = false;
    this.messageHandler = null;
    this._isInitialized = false;
  }

  // 重新初始化
  public reinitialize(): void {
    this.destroy();
    this.setupCommunication();
  }
}

// 导出单例实例
export const twaBridge = TwaBridge.getInstance();

// 导出便捷方法
export const requestTwaData = (type: string, data?: Record<string, unknown>) =>
  twaBridge.requestData(type, data);
export const sendTwaMessage = (message: TwaMessage) =>
  twaBridge.sendMessage(message);
export const isTwaConnected = () => twaBridge.isTwaConnected();
export const isTwaInitialized = () => twaBridge.isInitialized();
export const getTwaPort = () => twaBridge.getPort();
export const setTwaMessageHandler = (
  handler: (data: Record<string, unknown>) => void,
) => twaBridge.setMessageHandler(handler);
export const destroyTwaBridge = () => twaBridge.destroy();
export const reinitializeTwaBridge = () => twaBridge.reinitialize();
export const setupTwaCommunication = () => twaBridge.setupCommunication();
